import { MouseEvent, useState } from 'react'
import { Popover } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { replenishmentPlanActions } from '@/data/replenishmentPlan/slice/replenishmentPlan.slice.ts'
import {
  selectSelectedSpecies,
  selectSpecies,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { Species } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { useTrackReplenishmentPlanAnalyticsEvent } from '@/core/hooks/useTrackReplenishmentPlanAnalyticsEvent.ts'
import {
  DatagridActionsEvent,
  DatagridActionsType,
} from '@/core/analytics/events/replenishmentPlan/DatagridActionsEvent.ts'
import ChevronDownIcon from '@/core/assets/icons/chevron-down.svg'
import {
  ApplyButton,
  ChevronWrapper,
  ClearButton,
  Footer,
  FooterActions,
  OptionLabel,
  OptionRow,
  OptionsGrid,
  SelectedText,
  StyledCheckbox,
  TabButton,
  TabsRow,
  Trigger,
} from './SpeciesSelector.styles.ts'

type SpeciesGroup = 'pest' | 'animal'

const SpeciesSelector = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { trackReplenishmentPlanAnalyticsEvent } =
    useTrackReplenishmentPlanAnalyticsEvent()

  const species = useSelector(selectSpecies)
  // Applied filter (drives the plan). The panel edits a local draft and only commits on Apply.
  const selectedSpecies = useSelector(selectSelectedSpecies)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tab, setTab] = useState<SpeciesGroup>('pest')
  const [pending, setPending] = useState<Species[]>([])

  const pendingSanaIds = new Set(pending.map((item) => item.sanaId))

  // Applied label reflects the committed filter, not the in-progress draft.
  const getLabel = () => {
    if (selectedSpecies.length === 0) {
      return t('speciesSelector.allPestsAnimals')
    }

    if (selectedSpecies.length === 1) {
      return selectedSpecies[0]?.name ?? ''
    }

    return `${selectedSpecies.length} selected`
  }

  const open = (event: MouseEvent<HTMLElement>) => {
    setPending(selectedSpecies) // seed the draft from the applied filter
    setAnchorEl(event.currentTarget)
  }

  const toggle = (item: Species) => {
    setPending((prev) =>
      pendingSanaIds.has(item.sanaId)
        ? prev.filter((selected) => selected.sanaId !== item.sanaId)
        : [...prev, item],
    )
  }

  const apply = () => {
    trackReplenishmentPlanAnalyticsEvent(
      new DatagridActionsEvent({ type: DatagridActionsType.SpeciesFilter }),
    )
    dispatch(replenishmentPlanActions.setSelectedSpecies(pending))
    setAnchorEl(null)
  }

  const options = tab === 'pest' ? species.pest : species.animal

  return (
    <>
      <Trigger
        onClick={open}
        endIcon={
          <ChevronWrapper className={anchorEl ? 'open' : ''}>
            <ChevronDownIcon />
          </ChevronWrapper>
        }
      >
        {getLabel()}
      </Trigger>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, mt: 0.5, width: 720, overflow: 'hidden' },
          },
        }}
      >
        <TabsRow>
          <TabButton active={tab === 'pest'} onClick={() => setTab('pest')}>
            {t('speciesSelector.pest')}
          </TabButton>
          <TabButton active={tab === 'animal'} onClick={() => setTab('animal')}>
            {t('speciesSelector.animal')}
          </TabButton>
        </TabsRow>

        <OptionsGrid>
          {options.map((item) => {
            const checked = pendingSanaIds.has(item.sanaId)

            return (
              <OptionRow
                key={item.sanaId}
                checked={checked}
                onClick={() => toggle(item)}
              >
                <StyledCheckbox size="small" checked={checked} />
                <OptionLabel checked={checked}>{item.name}</OptionLabel>
              </OptionRow>
            )
          })}
        </OptionsGrid>

        <Footer>
          <SelectedText>
            {t('speciesSelector.selected', { count: pending.length })}
          </SelectedText>

          <FooterActions>
            <ClearButton onClick={() => setPending([])}>
              {t('speciesSelector.clear')}
            </ClearButton>
            <ApplyButton variant="contained" onClick={apply}>
              {t('speciesSelector.apply')}
            </ApplyButton>
          </FooterActions>
        </Footer>
      </Popover>
    </>
  )
}

export default SpeciesSelector
